import React, { forwardRef } from "react";
import { formatCurrency, formatDate } from "../utils/formatters.js";
import { resolvePaymentMethodName } from "../utils/paymentUtils.js";
import { COMPANY_NAME, COMPANY_RIF, COMPANY_LOGO } from "../utils/company.js";

const summaryRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 0",
  fontSize: 13,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid #e5e7eb",
};

/**
 * Tarjeta estática para captura (screenshot) de factura — no depende de Tailwind en el DOM capturado.
 */
const InvoiceShareCard = forwardRef(function InvoiceShareCard(
  { invoice, paymentMethods = [] },
  ref,
) {
  if (!invoice) return null;

  const items = invoice.items || [];
  const payments = invoice.payments || [];
  const totalPaid = Math.max(0, (invoice.totalAmount || 0) - (invoice.pendingAmount || 0));
  const creditApplied = invoice.creditApplied || 0;
  const invoiceDate = invoice.date
    ? formatDate(new Date(invoice.date))
    : invoice.createdAt
      ? formatDate(new Date(invoice.createdAt))
      : "—";

  return (
    <div
      ref={ref}
      style={{
        width: 420,
        background: "#ffffff",
        color: "#111827",
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: 13,
        lineHeight: 1.45,
        padding: "28px 24px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <img
          src={COMPANY_LOGO}
          alt={COMPANY_NAME}
          width={72}
          height={72}
          style={{ display: "block", margin: "0 auto 8px", objectFit: "contain" }}
        />
        <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>{COMPANY_NAME}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>RIF {COMPANY_RIF}</div>
      </div>

      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>FACTURA</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#4f46e5" }}>
          #{invoice.invoiceNumber}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{invoiceDate}</div>
      </div>

      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "12px 14px",
          marginBottom: 18,
        }}
      >
        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
          Cliente
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
          {invoice.client?.name || "Cliente no registrado"}
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#64748b", marginBottom: 8 }}>
        Detalle de productos
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            background: "#f1f5f9",
            padding: "8px 12px",
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#475569",
          }}
        >
          <span style={{ flex: 1 }}>Producto</span>
          <span style={{ width: 72, textAlign: "right" }}>Total</span>
        </div>
        {items.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>Sin ítems</div>
        ) : (
          items.map((item, index) => (
            <div key={index} style={{ ...rowStyle, padding: "10px 12px", margin: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
                  {item.byWeight
                    ? `${item.quantity} u · ${item.weight} kg @ ${formatCurrency(item.price)}/kg`
                    : `${item.quantity} x ${formatCurrency(item.price)}`}
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>
                {formatCurrency(item.total)}
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ borderTop: "2px solid #111827", paddingTop: 12, marginBottom: 16 }}>
        <div style={summaryRow}>
          <span>Total factura</span>
          <strong>{formatCurrency(invoice.totalAmount)}</strong>
        </div>
        {creditApplied > 0 && (
          <div style={summaryRow}>
            <span>Crédito aplicado</span>
            <strong style={{ color: "#059669" }}>- {formatCurrency(creditApplied)}</strong>
          </div>
        )}
        <div style={summaryRow}>
          <span>Total pagado</span>
          <strong style={{ color: "#059669" }}>{formatCurrency(totalPaid)}</strong>
        </div>
        <div
          style={{
            ...summaryRow,
            fontSize: 15,
            fontWeight: 800,
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px dashed #cbd5e1",
          }}
        >
          <span>Saldo pendiente</span>
          <strong style={{ color: "#dc2626" }}>{formatCurrency(invoice.pendingAmount)}</strong>
        </div>
      </div>

      {payments.length > 0 && (
        <>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#64748b",
              marginBottom: 8,
            }}
          >
            Pagos registrados
          </div>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "4px 12px",
              marginBottom: 16,
            }}
          >
            {payments.map((payment, index) => (
              <div
                key={payment._id || index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: index < payments.length - 1 ? "1px solid #f1f5f9" : "none",
                  fontSize: 12,
                }}
              >
                <span>
                  {resolvePaymentMethodName(payment, paymentMethods)} ·{" "}
                  {payment.date
                    ? formatDate(new Date(payment.date))
                    : payment.createdAt
                      ? formatDate(new Date(payment.createdAt))
                      : ""}
                </span>
                <strong>{formatCurrency(payment.amount, payment.currency)}</strong>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          textAlign: "center",
          fontSize: 11,
          color: "#94a3b8",
          borderTop: "1px solid #e5e7eb",
          paddingTop: 14,
        }}
      >
        Documento informativo · {COMPANY_NAME}
        <br />
        RIF {COMPANY_RIF}
        <br />
        Gracias por su preferencia
      </div>
    </div>
  );
});

export default InvoiceShareCard;
